import { Button, DropdownMenu, Text } from '@radix-ui/themes';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Combobox from '~/components/Combobox';
import CSVInput from '~/components/CSVInput';
import Icon from '~/components/Icon';
import { columnsControlShape } from '../Model/use-columns-control';
import { filterType } from './types';

const ToolbarGhostButton = styled(Button)`
  height: var(--base-button-height);
  box-sizing: border-box;

  &:focus {
    ${({ $suppressOutline }) => $suppressOutline && 'outline: none;'}
  }
`;

function FilterDropdown({ filterOrder, filters, visibleFilters, onSelectFilterKey }) {
  const [suppressOutline, setSuppressOutline] = useState(false);

  const filterOptions = filterOrder
    .map(key => filters.find(f => f.key === key))
    .filter(f => f)
    .filter(f => !visibleFilters.includes(f.key) && !f.hide);
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <ToolbarGhostButton
          variant='ghost'
          color='gray'
          highContrast
          m='2'
          $suppressOutline={suppressOutline}
        >
          <Icon
            name='plus'
            width={16}
            height={16}
            inline
          />
          Filters
        </ToolbarGhostButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content variant='soft'>
        {filterOptions.length === 0 && (
          <Text
            color='gray'
            size='2'
            mx='2'
            my='1'
          >
            All filters added
          </Text>
        )}
        {filterOptions.map(filter => {
          return (
            <DropdownMenu.Item
              color='gray'
              key={filter.key}
              onSelect={() => {
                setSuppressOutline(true);
                setTimeout(() => setSuppressOutline(false), 1000);
                onSelectFilterKey(filter.key);
              }}
            >
              <Text highContrast>{filter.label}</Text>
            </DropdownMenu.Item>
          );
        })}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

function RenderFilters({
  visibleFilters,
  filters,
  comboboxRefs,
  textInputRefs,
  justAddedFilterKeyRef,
  onSelectFilterKey,
}) {
  return visibleFilters.map(key => {
    const filter = filters.find(f => f.key === key);
    if (!filter || filter.hide) return null;
    if (filter.type === 'csv') {
      const { key, ...rest } = filter;
      return (
        <CSVInput
          key={key}
          csvKey={key}
          ref={el => {
            textInputRefs.current[key] = el;
          }}
          onRemove={() => onSelectFilterKey(key)}
          defaultOpen={justAddedFilterKeyRef.current === filter.key}
          {...rest}
        />
      );
    }

    if (filter.type === 'combobox') {
      return (
        <Combobox
          key={filter.key}
          ref={el => {
            comboboxRefs.current[filter.key] = el;
          }}
          renderLabelInElementFareLeft
          showSingleItemAsMultipleItem
          showAnyIfNoValueIsSelected
          defaultOpen={justAddedFilterKeyRef.current === filter.key}
          onComboboxRemove={() => onSelectFilterKey(filter.key)}
          selectedItemsOnTop
          {...filter}
        />
      );
    }

    console.warn(`Filter key "${filter.key}" has unknown filter type: ${filter.type}`);

    return null;
  });
}

function SearchFilters({ model, columnsControl, columnsToFiltersMapping }) {
  const comboboxRefs = useRef({});
  const textInputRefs = useRef({});
  const justAddedFilterKeyRef = useRef(null);
  const [visibleFilters, setVisibleFilters] = useState([]);
  const [dropdownFilterOrder, setDropdownFilterOrder] = useState([]);

  useEffect(() => {
    const columnOptionKeys = columnsControl?.columnOptions?.map(c => c.key) ?? [];

    const filtersToColumnKeys = Object.fromEntries(
      Object.entries(columnsToFiltersMapping ?? {}).map(([colKey, filterKey]) => [filterKey, colKey]),
    );

    const { shownFilters, hiddenFilters } = model.filters.reduce(
      (acc, f) => {
        const columnKey = filtersToColumnKeys[f.key] ?? f.key;

        if (!columnOptionKeys.includes(columnKey)) {
          acc.shownFilters.push(f);
          return acc;
        }

        const column = columnsControl.columnOptions.find(c => c.key === columnKey);
        const bucket = column?.selected ? acc.shownFilters : acc.hiddenFilters;
        bucket.push(f);
        return acc;
      },
      { shownFilters: [], hiddenFilters: [] },
    );

    const hiddenFilterKeys = new Set(hiddenFilters.map(f => f.key));

    setVisibleFilters(prev => {
      const filtersToRemove = prev.filter(k => hiddenFilterKeys.has(k));

      for (const key of filtersToRemove) {
        comboboxRefs.current[key]?.clearSelected?.();
        if (textInputRefs.current[key]) textInputRefs.current[key].value = '';
        model.filters.find(f => f.key === key)?.clear?.();
      }

      const visibleKeys = shownFilters.filter(f => f.defaultSelected || f.value).map(f => f.key);

      const prevWithoutHidden = prev.filter(k => !hiddenFilterKeys.has(k));
      return [...new Set([...prevWithoutHidden, ...visibleKeys])];
    });

    const shownFiltersKeys = shownFilters.filter(f => !hiddenFilterKeys.has(f.key)).map(f => f.key);

    setDropdownFilterOrder([
      ...shownFiltersKeys.filter(k => shownFilters.find(f => f.key === k && (f.defaultSelected || f.value))),
      ...shownFiltersKeys.filter(k => shownFilters.find(f => f.key === k && !f.defaultSelected && !f.value)),
    ]);
  }, [columnsControl.columnOptions, model.filters, columnsToFiltersMapping]);

  function onSelectFilterKey(key) {
    if (visibleFilters.includes(key)) {
      if (comboboxRefs.current[key]?.clearSelected) {
        comboboxRefs.current[key].clearSelected();
      }
      if (textInputRefs.current[key]) {
        textInputRefs.current[key].value = '';
      }
      const filter = model.filters.find(f => f.key === key);
      if (filter?.clear) {
        filter.clear();
      }
      setVisibleFilters(visibleFilters.filter(k => k !== key));
    } else {
      justAddedFilterKeyRef.current = key;
      setVisibleFilters([...visibleFilters, key]);
    }
  }

  useEffect(() => {
    model.apply();
  }, [model]);

  function resetFilters() {
    Object.values(comboboxRefs.current).forEach(ref => {
      ref?.clearSelected?.();
    });
    const textFilters = model.filters.filter(filter => filter.type === 'text');
    textFilters.forEach(filter => {
      filter.clear();
    });
    Object.values(textInputRefs.current).forEach(ref => {
      if (ref) ref.value = '';
    });
    model.clear();
    setVisibleFilters([]);
  }

  function ResetButton() {
    return (
      <ToolbarGhostButton
        variant='ghost'
        color='gray'
        highContrast
        onClick={resetFilters}
        ml='2'
      >
        <Icon
          name='refresh-cw-05'
          width={16}
          height={16}
          inline
        />
        Reset filters
      </ToolbarGhostButton>
    );
  }

  return (
    <>
      <RenderFilters
        visibleFilters={visibleFilters}
        filters={model.filters}
        comboboxRefs={comboboxRefs}
        textInputRefs={textInputRefs}
        justAddedFilterKeyRef={justAddedFilterKeyRef}
        onSelectFilterKey={onSelectFilterKey}
      />
      <FilterDropdown
        filterOrder={dropdownFilterOrder}
        filters={model.filters}
        visibleFilters={visibleFilters}
        onSelectFilterKey={onSelectFilterKey}
      />
      {visibleFilters.length > 0 && <ResetButton />}
    </>
  );
}

FilterDropdown.propTypes = {
  filterOrder: PropTypes.arrayOf(PropTypes.string).isRequired,
  filters: PropTypes.array.isRequired,
  visibleFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectFilterKey: PropTypes.func.isRequired,
};

SearchFilters.propTypes = {
  model: filterType,
  columnsControl: columnsControlShape,
  columnsToFiltersMapping: PropTypes.objectOf(PropTypes.string),
};

export default SearchFilters;
